import * as React from "react";

import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";

type ConfirmExternalLinkProps = {
  href: string;
  children: React.ReactNode;
  className?: string;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  openInNewTab?: boolean;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  showDestination?: boolean;
};

const getHostname = (href: string) => {
  try {
    return new URL(href).hostname;
  } catch {
    return "";
  }
};

const shouldBypassInterception = (
  event: React.MouseEvent<HTMLAnchorElement>
) => {
  if (event.defaultPrevented) return true;
  if (event.button !== 0) return true;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return true;
  return false;
};

const openExternalUrl = ({
  href,
  openInNewTab,
}: {
  href: string;
  openInNewTab: boolean;
}) => {
  if (openInNewTab) {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.assign(href);
};

const ConfirmExternalLink = ({
  href,
  children,
  className,
  title = "Abrir link externo?",
  description,
  confirmLabel = "Acessar",
  cancelLabel = "Cancelar",
  openInNewTab = true,
  target,
  rel = "noopener noreferrer",
  showDestination = true,
}: ConfirmExternalLinkProps) => {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const destinationHost = React.useMemo(() => getHostname(href), [href]);

  const resolvedTarget = target ?? (openInNewTab ? "_blank" : undefined);

  const resolvedDescription =
    description ??
    "Você será direcionado para um site externo ao CIB. Deseja continuar?";

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (shouldBypassInterception(event)) return;

    event.preventDefault();
    setOpen(true);
  };

  const handleConfirm = () => {
    setOpen(false);

    window.setTimeout(() => {
      openExternalUrl({ href, openInNewTab });
    }, 0);
  };

  const DescriptionBlock = (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground leading-relaxed">
        {resolvedDescription}
      </p>
      {showDestination && destinationHost ? (
        <p className="text-sm text-muted-foreground">
          Destino:{" "}
          <span className="font-medium text-foreground">{destinationHost}</span>
        </p>
      ) : null}
    </div>
  );

  return (
    <>
      <a
        href={href}
        target={resolvedTarget}
        rel={rel}
        className={className}
        onClick={handleClick}
      >
        {children}
      </a>

      {isMobile ? (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle className="text-xl font-semibold">{title}</DrawerTitle>
              <DrawerDescription className="pt-1">{DescriptionBlock}</DrawerDescription>
            </DrawerHeader>

            <DrawerFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-full h-14"
              >
                {cancelLabel}
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                className="rounded-full h-14"
              >
                <ExternalLink className="w-4 h-4" />
                {confirmLabel}
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl">{title}</DialogTitle>
              <DialogDescription>{DescriptionBlock}</DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-full h-12"
              >
                {cancelLabel}
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                className="rounded-full h-12"
              >
                <ExternalLink className="w-4 h-4" />
                {confirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ConfirmExternalLink;
